import { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Grid,
  TextField,
  Alert,
  Snackbar,
  CircularProgress,
  Slider,
  IconButton,
  Paper
} from '@mui/material';
import {
  Favorite,
  Save,
  Cancel,
  ArrowBack
} from '@mui/icons-material';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import { vitalsAPI } from '../../services/api';

function VitalsRecording() {
  const { admissionId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const patientInfo = location.state || {};

  const [vitals, setVitals] = useState({
    bloodPressure: null,
    temperature: null,
    pulseRate: null,
    respiratoryRate: null,
    weight: null,
    height: null,
    bmi: null,
    oxygenSaturation: null,
    rbs: null,
    insulinUnits: null,
    painScale: null,
    notes: ''
  });

  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);

  const handleVitalsChange = (e) => {
    const { name, value } = e.target;
    setVitals({ ...vitals, [name]: value });

    // Auto-calculate BMI
    if (name === 'weight' || name === 'height') {
      const weight = name === 'weight' ? parseFloat(value) : parseFloat(vitals.weight);
      const height = name === 'height' ? parseFloat(value) : parseFloat(vitals.height);

      if (weight && height) {
        const bmi = (weight / ((height / 100) ** 2)).toFixed(2);
        setVitals(prev => ({ ...prev, bmi }));
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    // Validate at least one vital is entered
    const hasData = Object.entries(vitals).some(([key, value]) =>
      key !== 'bmi' && key !== 'notes' && value !== ''
    );

    if (!hasData) {
      setError('Please enter at least one vital sign');
      setLoading(false);
      return;
    }

    try {
      await vitalsAPI.recordVitals(admissionId, patientInfo.patientId, {
        ...vitals,
        recordedAt: new Date().toISOString()
      });

      setSuccess('Vitals recorded successfully!');

      // Reset form after successful submission
      setTimeout(() => {
        setVitals({
          bloodPressure: '',
          temperature: '',
          pulseRate: '',
          respiratoryRate: '',
          weight: '',
          height: '',
          bmi: '',
          oxygenSaturation: '',
          rbs: '',
          insulinUnits: '',
          painScale: '',
          notes: ''
        });
        setSuccess('');
      }, 2000);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to record vitals. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box>
      <Card elevation={2}>
        <CardContent sx={{ p: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
            <IconButton onClick={() => navigate('/nursing')} sx={{ mr: 2 }}>
              <ArrowBack />
            </IconButton>
            <Favorite sx={{ fontSize: 40, color: 'error.main', mr: 2 }} />
            <Box sx={{ flexGrow: 1 }}>
              <Typography variant="h5" component="h2" fontWeight="bold">
                Record Vitals
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Patient: {patientInfo.patientName} ({patientInfo.patientCode}) • Room: {patientInfo.roomNumber}
              </Typography>
            </Box>
          </Box>

          {error && (
            <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
              {error}
            </Alert>
          )}

          <Snackbar
            open={!!success}
            autoHideDuration={3000}
            onClose={() => setSuccess('')}
            anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
          >
            <Alert severity="success" onClose={() => setSuccess('')}>
              {success}
            </Alert>
          </Snackbar>

          <form onSubmit={handleSubmit}>
            <Paper elevation={1} sx={{ p: 3, mb: 3, bgcolor: 'grey.50' }}>
              <Typography variant="h6" fontWeight="bold" sx={{ mb: 2 }}>
                Vital Signs
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6} md={3}>
                  <TextField
                    fullWidth
                    label="Blood Pressure"
                    name="bloodPressure"
                    value={vitals.bloodPressure}
                    onChange={handleVitalsChange}
                    placeholder="120/80"
                    helperText="e.g., 120/80"
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <TextField
                    fullWidth
                    type="number"
                    label="Temperature (°F)"
                    name="temperature"
                    value={vitals.temperature}
                    onChange={handleVitalsChange}
                    inputProps={{ step: 0.1, min: 90, max: 110 }}
                    helperText="Normal: 97-99°F"
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <TextField
                    fullWidth
                    type="number"
                    label="Pulse Rate (bpm)"
                    name="pulseRate"
                    value={vitals.pulseRate}
                    onChange={handleVitalsChange}
                    inputProps={{ min: 30, max: 200 }}
                    helperText="Normal: 60-100 bpm"
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <TextField
                    fullWidth
                    type="number"
                    label="Respiratory Rate"
                    name="respiratoryRate"
                    value={vitals.respiratoryRate}
                    onChange={handleVitalsChange}
                    inputProps={{ min: 5, max: 60 }}
                    helperText="Normal: 12-20 /min"
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <TextField
                    fullWidth
                    type="number"
                    label="Weight (kg)"
                    name="weight"
                    value={vitals.weight}
                    onChange={handleVitalsChange}
                    inputProps={{ step: 0.1, min: 1 }}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <TextField
                    fullWidth
                    type="number"
                    label="Height (cm)"
                    name="height"
                    value={vitals.height}
                    onChange={handleVitalsChange}
                    inputProps={{ step: 0.1, min: 30 }}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <TextField
                    fullWidth
                    type="number"
                    label="BMI"
                    name="bmi"
                    value={vitals.bmi}
                    InputProps={{ readOnly: true }}
                    sx={{ bgcolor: 'grey.200' }}
                    helperText="Auto-calculated"
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <TextField
                    fullWidth
                    type="number"
                    label="Oxygen Saturation (%)"
                    name="oxygenSaturation"
                    value={vitals.oxygenSaturation}
                    onChange={handleVitalsChange}
                    inputProps={{ min: 0, max: 100 }}
                    helperText="Normal: >95%"
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <TextField
                    fullWidth
                    type="number"
                    label="RBS (mg/dL)"
                    name="rbs"
                    value={vitals.rbs}
                    onChange={handleVitalsChange}
                    inputProps={{ step: 0.1, min: 0, max: 600 }}
                    helperText="Random Blood Sugar"
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <TextField
                    fullWidth
                    type="number"
                    label="Insulin Units"
                    name="insulinUnits"
                    value={vitals.insulinUnits}
                    onChange={handleVitalsChange}
                    inputProps={{ step: 0.1, min: 0, max: 100 }}
                    helperText="Units administered"
                  />
                </Grid>
                <Grid item xs={12}>
                  <Box sx={{ px: 2, pt: 2 }}>
                    <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                      Pain Scale (0-10 Numerical Rating Scale)
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <Slider
                        value={vitals.painScale ? parseInt(vitals.painScale) : 0}
                        onChange={(_, value) => setVitals({ ...vitals, painScale: value })}
                        min={0}
                        max={10}
                        step={1}
                        marks
                        valueLabelDisplay="on"
                        sx={{ flexGrow: 1 }}
                      />
                      <TextField
                        type="number"
                        name="painScale"
                        value={vitals.painScale}
                        onChange={handleVitalsChange}
                        inputProps={{ min: "0", max: "10", step: "1" }}
                        sx={{ width: 80 }}
                        size="small"
                      />
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 1 }}>
                      <Typography variant="caption" color="text.secondary">😊 0 - No Pain</Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ textAlign: 'center' }}>
                        {vitals.painScale !== '' && vitals.painScale !== null && (
                          <>
                            {parseInt(vitals.painScale) === 0 && '😊 No Pain'}
                            {parseInt(vitals.painScale) >= 1 && parseInt(vitals.painScale) <= 3 && '😐 Mild Pain'}
                            {parseInt(vitals.painScale) >= 4 && parseInt(vitals.painScale) <= 6 && '😣 Moderate Pain'}
                            {parseInt(vitals.painScale) >= 7 && parseInt(vitals.painScale) <= 9 && '😖 Severe Pain'}
                            {parseInt(vitals.painScale) === 10 && '😱 Worst Pain'}
                          </>
                        )}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">😱 10 - Worst Pain</Typography>
                    </Box>
                  </Box>
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    multiline
                    rows={3}
                    label="Additional Notes"
                    name="notes"
                    value={vitals.notes}
                    onChange={handleVitalsChange}
                    placeholder="Any observations, patient complaints, or additional information..."
                  />
                </Grid>
              </Grid>
            </Paper>

            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
              <Button
                type="button"
                variant="outlined"
                startIcon={<Cancel />}
                onClick={() => navigate('/nursing')}
                disabled={loading}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                variant="contained"
                color="primary"
                startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <Save />}
                disabled={loading}
                size="large"
              >
                {loading ? 'Saving...' : 'Save Vitals'}
              </Button>
            </Box>
          </form>
        </CardContent>
      </Card>
    </Box>
  );
}

export default VitalsRecording;